export const API_BASE_URL = 'https://api.heliosaggregate.com';

// ─── Auth token (JWT) ───────────────────────────────────────────────────────
export const AUTH_TOKEN_KEY = 'helios_auth_token';
export function getToken(): string | null {
  try { return localStorage.getItem(AUTH_TOKEN_KEY); } catch { return null; }
}
/**
 * fetch() that attaches the JWT (if present) and, on 401, clears the session and
 * bounces to /login. Use for the auth-gated endpoints (/owner/*, /info/*).
 */
export async function authFetch(url: string, init: RequestInit = {}): Promise<Response> {
  const token = getToken();
  const headers = new Headers(init.headers || {});
  if (token) headers.set('Authorization', `Bearer ${token}`);
  const res = await fetch(url, { ...init, headers });
  if (res.status === 401 && typeof window !== 'undefined') {
    try {
      localStorage.removeItem(AUTH_TOKEN_KEY);
      localStorage.removeItem('helios_auth_user');
    } catch { /* ignore */ }
    if (!window.location.pathname.startsWith('/login')) window.location.href = '/login';
  }
  return res;
}

// ─── Error ────────────────────────────────────────────────────────────────────
export class ApiError extends Error {
  status: number;
  data: unknown;
  url: string;

  constructor(message: string, status: number, data?: unknown, url?: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.data = data;
    this.url = url ?? '';
  }
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

/** Recursively strip undefined values from an object so JSON.stringify omits them */
function stripUndefined(obj: unknown): unknown {
  if (obj === null || obj === undefined) return obj;
  if (Array.isArray(obj)) return obj.map(stripUndefined);
  if (typeof obj === 'object') {
    return Object.fromEntries(
      Object.entries(obj as Record<string, unknown>)
        .filter(([, v]) => v !== undefined)
        .map(([k, v]) => [k, stripUndefined(v)])
    );
  }
  return obj;
}

/** Detect if an error is a CORS / network failure */
function isCorsOrNetworkError(err: unknown): boolean {
  if (!(err instanceof Error)) return false;
  const msg = err.message.toLowerCase();
  return (
    msg.includes('failed to fetch') ||
    msg.includes('networkerror') ||
    msg.includes('cors') ||
    msg.includes('cross-origin') ||
    msg.includes('network request failed')
  );
}

// ─── Core request ─────────────────────────────────────────────────────────────
interface RequestOptions {
  method?: string;
  headers?: Record<string, string>;
  body?: string;
  signal?: AbortSignal;
}

async function request<T>(path: string, options?: RequestOptions): Promise<T> {
  const url = `${API_BASE_URL}${path}`;
  const method = options?.method ?? 'GET';

  // Debug log — visible in browser DevTools console
  console.debug(`[API] ${method} ${url}`, options?.body ? JSON.parse(options.body) : '');

  let res: Response;
  try {
    res = await fetch(url, {
      method,
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        ...options?.headers,
      },
      body: options?.body,
      signal: options?.signal,
    });
  } catch (networkErr) {
    const isCors = isCorsOrNetworkError(networkErr);
    const message = networkErr instanceof Error ? networkErr.message : 'Network error';
    if (!options?.signal?.aborted) console.error(`[API] Network error on ${method} ${url}:`, networkErr);

    if (isCors) {
      throw new ApiError(
        `CORS / Network error: The server at ${API_BASE_URL} blocked this request. Please enable CORS on your backend for origin https://cabinet.heliosaggregate.com (methods: GET, POST, PATCH, PUT, DELETE, OPTIONS).`,
        0,
        null,
        url
      );
    }

    throw new ApiError(`Network error: ${message}`, 0, null, url);
  }

  // Parse body regardless of status
  let responseData: unknown = null;
  const contentType = res.headers.get('content-type') ?? '';
  if (res.status !== 204 && contentType.includes('application/json')) {
    try {
      responseData = await res.json();
    } catch {
      responseData = null;
    }
  }

  if (!res.ok) {
    const errorMessage =
      (responseData as { message?: string; error?: string })?.message ??
      (responseData as { message?: string; error?: string })?.error ??
      `HTTP ${res.status} ${res.statusText}`;

    if (res.status >= 500) {
      console.error(`[API] ${method} ${url} → ${res.status}`, responseData);
    } else {
      console.warn(`[API] ${method} ${url} → ${res.status}`, responseData);
    }
    throw new ApiError(errorMessage, res.status, responseData, url);
  }

  console.debug(`[API] ${method} ${url} → ${res.status}`, responseData);
  return (res.status === 204 ? undefined : responseData) as T;
}

// ─── Public API ───────────────────────────────────────────────────────────────
export const api = {
  get: <T>(path: string, signal?: AbortSignal) =>
    request<T>(path, { method: 'GET', signal }),

  post: <T>(path: string, body: unknown, signal?: AbortSignal) =>
    request<T>(path, {
      method: 'POST',
      body: JSON.stringify(stripUndefined(body)),
      signal,
    }),

  patch: <T>(path: string, body: unknown, signal?: AbortSignal) =>
    request<T>(path, {
      method: 'PATCH',
      body: JSON.stringify(stripUndefined(body)),
      signal,
    }),

  put: <T>(path: string, body: unknown, signal?: AbortSignal) =>
    request<T>(path, {
      method: 'PUT',
      body: JSON.stringify(stripUndefined(body)),
      signal,
    }),

  delete: <T>(path: string, signal?: AbortSignal) =>
    request<T>(path, { method: 'DELETE', signal }),
};

// ─── Response type helpers ────────────────────────────────────────────────────

export interface PaginatedResponse<T> {
  data: T[];
  total: number;
  page: number;
  pageSize: number;
}

/** Extract a human-readable error message from any thrown value */
export function getApiErrorMessage(err: unknown, fallback = 'An unexpected error occurred'): string {
  if (err instanceof ApiError) {
    // Try to extract nested validation errors
    const data = err.data as { errors?: Array<{ field: string; message: string }> } | null;
    if (data?.errors && Array.isArray(data.errors) && data.errors.length > 0) {
      return data.errors.map((e) => `${e.field}: ${e.message}`).join('; ');
    }
    return err.message;
  }
  if (err instanceof Error) return err.message;
  return fallback;
}